import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  createUiQuality,
  startFrameTelemetry,
  type UiQuality,
  type UiQualityWindow,
} from '@ovasabi/runtime-browser'

/**
 * Feeds measured frame windows to the app's one `uiQuality`
 * (foundation docs/ui_render_performance_research.md, P1 + P2).
 *
 * The tier is written as `data-ui-tier` on the root element; styles/theme.ts
 * and ui-minimal select cheaper blur, shadow and motion from it.
 *
 * Measurement runs only inside a window: a `frameTelemetry` session opens when
 * the main scroller (`#main-content`, MobileShell) starts scrolling or the
 * route changes, and closes after SCROLL_IDLE_MS without a scroll event, or
 * once the route has had ROUTE_SETTLE_MS. Its stats go to `recordWindow`,
 * which ignores short or degraded windows and demotes only on sustained
 * slow-frame share.
 *
 * Hot path: one passive, capturing `scroll` listener on the document. Per event
 * it compares the target's id and moves a deadline number; the idle timer is
 * armed once per session and re-arms itself from the deadline, so a scroll
 * event never touches a timer.
 */

export const MAIN_SCROLLER_ID = 'main-content'
export const SCROLL_IDLE_MS = 300
/** Long enough to cover a route's mount and header entrance (~280 ms). */
export const ROUTE_SETTLE_MS = 1000

export type RenderQualityScope = 'scroll' | 'route'

type VisibilityDocument = Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'>

export interface RenderQualityOptions {
  quality: Pick<UiQuality, 'recordWindow'>
  /** Defaults to the global document. */
  document?: VisibilityDocument | null
  /** Defaults to `startFrameTelemetry('choosechow.<scope>')`. */
  startTelemetry?: (scope: RenderQualityScope) => { stop(): UiQualityWindow }
  now?: () => number
  idleMs?: number
  routeSettleMs?: number
}

export interface RenderQualityController {
  /** Open (or extend) a measured window for a route change. */
  noteRouteChange(): void
  /** True while a telemetry session is open. */
  measuring(): boolean
  /** Remove listeners and stop any open session without recording it. */
  dispose(): void
}

const SCROLL_LISTENER: AddEventListenerOptions = { passive: true, capture: true }

export function watchRenderQuality(options: RenderQualityOptions): RenderQualityController {
  const doc = options.document !== undefined ? options.document : typeof document !== 'undefined' ? document : null
  const startTelemetry =
    options.startTelemetry ?? ((scope: RenderQualityScope) => startFrameTelemetry(`choosechow.${scope}`))
  const now = options.now ?? (() => performance.now())
  const idleMs = options.idleMs ?? SCROLL_IDLE_MS
  const routeSettleMs = options.routeSettleMs ?? ROUTE_SETTLE_MS

  let session: { stop(): UiQualityWindow } | null = null
  let deadline = 0
  let timer: ReturnType<typeof setTimeout> | null = null
  let disposed = false

  const close = (record: boolean) => {
    if (timer !== null) clearTimeout(timer)
    timer = null
    const open = session
    session = null
    deadline = 0
    if (!open) return
    const stats = open.stop()
    if (record) options.quality.recordWindow(stats)
  }

  const onDeadline = () => {
    timer = null
    const remaining = deadline - now()
    if (remaining > 0) {
      timer = setTimeout(onDeadline, remaining)
      return
    }
    close(true)
  }

  const hold = (scope: RenderQualityScope, ms: number) => {
    if (disposed || !doc || doc.visibilityState !== 'visible') return
    const until = now() + ms
    if (until > deadline) deadline = until
    if (session) return
    session = startTelemetry(scope)
    timer = setTimeout(onDeadline, ms)
  }

  const onScroll = (event: Event) => {
    if ((event.target as { id?: string } | null)?.id === MAIN_SCROLLER_ID) hold('scroll', idleMs)
  }

  // Animation frames stop in a hidden document; close the window rather than
  // leave it open across the gap. frameTelemetry already excludes hidden time.
  const onVisibility = () => {
    if (doc?.visibilityState !== 'visible') close(true)
  }

  doc?.addEventListener('scroll', onScroll, SCROLL_LISTENER)
  doc?.addEventListener('visibilitychange', onVisibility)

  return {
    noteRouteChange: () => hold('route', routeSettleMs),
    measuring: () => session !== null,
    dispose() {
      if (disposed) return
      disposed = true
      doc?.removeEventListener('scroll', onScroll, SCROLL_LISTENER)
      doc?.removeEventListener('visibilitychange', onVisibility)
      close(false)
    },
  }
}

let sharedQuality: UiQuality | null = null

/**
 * The app's one uiQuality, created on first call (client only). main.tsx calls
 * it before the first render so the device prior's tier is on the root element
 * before first paint.
 */
export function sharedUiQuality(): UiQuality | null {
  if (sharedQuality) return sharedQuality
  if (typeof document === 'undefined') return null
  sharedQuality = createUiQuality()
  return sharedQuality
}

/**
 * Attaches the measured windows for the app's lifetime. Mount once, inside the
 * router. The first route is not measured: it is app start, and cold JIT,
 * shader and raster warm-up is a first-use hitch, not steady-state scroll
 * evidence (research doc §13, finding 6).
 */
export function useRenderQuality(overrides: Partial<RenderQualityOptions> = {}): void {
  const { pathname } = useLocation()
  const controller = useRef<RenderQualityController | null>(null)
  const lastPath = useRef(pathname)
  const initial = useRef(overrides)

  useEffect(() => {
    const quality = initial.current.quality ?? sharedUiQuality()
    if (!quality) return
    const watch = watchRenderQuality({ ...initial.current, quality })
    controller.current = watch
    return () => {
      watch.dispose()
      controller.current = null
    }
  }, [])

  useEffect(() => {
    if (lastPath.current === pathname) return
    lastPath.current = pathname
    controller.current?.noteRouteChange()
  }, [pathname])
}
